import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateInput } from '../shared/DateInput';

// m.6 Treasury, tab Priljevi (Vedran 24.09.2026): rucni unos ocekivanih priljeva.
// Priljev vrijedi dok se ne nadje na izvodu; crveno = prosao datum, nije na izvodu;
// zuto = isti partner na izvodu, ali iznos izvan 5 %, ceka potvrdu.

const T = { bg: '#17181c', panel: '#24262b', line: '#33363c', ink: '#f2f0ec', dim: '#9a9488', gold: '#c9a227', red: '#c05a5a', green: '#3fae6b' };

type Candidate = { entry_id: number; statement_date: string; amount: number; partner_name: string; diff_pct: number };
type InflowRow = {
    id: number; company_id: number; company_code: string | null; bank_account_id: number | null; iban: string | null; bank_name: string | null;
    partner_profile_id: number | null; partner_name: string; expected_date: string; amount: number; description: string | null;
    status: 'pending' | 'overdue' | 'fuzzy'; candidate: Candidate | null;
};
type Account = { id: number; company_id: number; iban: string; bank_name: string | null; account_label: string | null };
type Payload = {
    rows: InflowRow[];
    summary: { count: number; total: number; pending: number; overdue: number; fuzzy: number; overdue_count: number; fuzzy_count: number };
    companies: { id: number; code: string }[];
    accounts: Account[];
    today: string;
};
type Partner = { id: number; company_id: number; partner_code: string | null; name: string; oib: string | null };
type Form = { id: number | null; company_id: string; bank_account_id: string; partner_profile_id: number | null; partner_name: string; expected_date: string; amount: string; description: string };

const emptyForm = (companyId: string): Form => ({ id: null, company_id: companyId, bank_account_id: '', partner_profile_id: null, partner_name: '', expected_date: '', amount: '', description: '' });
const fmt = (n: number) => new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' €';
const fmtD = (d: string) => d.slice(0, 10).split('-').reverse().join('.') + '.';
const field: React.CSSProperties = { background: T.bg, color: T.ink, border: `1px solid ${T.line}`, borderRadius: 7, padding: '6px 9px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
const btn: React.CSSProperties = { background: 'transparent', border: `1px solid ${T.line}`, color: T.ink, borderRadius: 7, padding: '5px 11px', cursor: 'pointer', fontSize: 12 };

export function InflowsTab({ token, locale, companyId = '' }: { token: string; locale: 'hr' | 'en'; companyId?: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Payload | null>(null);
    const [err, setErr] = useState('');
    const [form, setForm] = useState<Form | null>(null);
    const [partners, setPartners] = useState<Partner[]>([]);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const headers = useMemo(() => ({ 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Bearer ${token}` }), [token]);

    const load = useCallback(() => {
        const q = companyId ? `?company_ids=${companyId}` : '';
        fetch(`/api/cashflow/expected-inflows${q}`, { headers })
            .then(async (r) => { if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.message ?? `HTTP ${r.status}`); } return r.json(); })
            .then((d: Payload) => { setData(d); setErr(''); })
            .catch((e: Error) => setErr(e.message));
    }, [companyId, headers]);
    useEffect(() => { load(); }, [load]);

    // Sifrarnik partnera: trazi od 2 znaka, za tvrtku iz obrasca.
    useEffect(() => {
        const q = form?.partner_name.trim() ?? '';
        if (!form || q.length < 2 || form.partner_profile_id !== null) { setPartners([]); return; }
        const h = window.setTimeout(() => {
            const c = form.company_id ? `&company_ids=${form.company_id}` : '';
            fetch(`/api/cashflow/expected-inflows/partners?q=${encodeURIComponent(q)}${c}`, { headers })
                .then((r) => (r.ok ? r.json() : { rows: [] }))
                .then((d) => setPartners(d.rows ?? []))
                .catch(() => setPartners([]));
        }, 250);
        return () => window.clearTimeout(h);
    }, [form?.partner_name, form?.company_id, form?.partner_profile_id, headers]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));

    const save = async () => {
        if (!form) return;
        const amt = Number(form.amount.replace(/\./g, '').replace(',', '.'));
        if (!form.company_id || !form.partner_name.trim() || !amt || Number.isNaN(amt) || amt <= 0) {
            setMsg(t('Popuni tvrtku, partnera i iznos (>0).', 'Fill company, partner and amount (>0).'));
            return;
        }
        setBusy(true); setMsg('');
        const body = {
            company_id: Number(form.company_id),
            bank_account_id: form.bank_account_id ? Number(form.bank_account_id) : null,
            partner_profile_id: form.partner_profile_id,
            partner_name: form.partner_name.trim(),
            expected_date: form.expected_date || null,
            amount: amt,
            description: form.description.trim() || null,
        };
        try {
            const r = await fetch(form.id ? `/api/cashflow/expected-inflows/${form.id}` : '/api/cashflow/expected-inflows', {
                method: form.id ? 'PUT' : 'POST', headers, body: JSON.stringify(body),
            });
            if (!r.ok) { const e = await r.json().catch(() => ({})); setMsg(e.message ?? t('Greška', 'Error')); setBusy(false); return; }
            setForm(null);
            load();
        } catch { setMsg(t('Greška', 'Error')); }
        setBusy(false);
    };

    const del = async (id: number) => {
        if (!window.confirm(t('Obrisati priljev?', 'Delete inflow?'))) return;
        await fetch(`/api/cashflow/expected-inflows/${id}`, { method: 'DELETE', headers }).catch(() => undefined);
        load();
    };

    const confirmMatch = async (r: InflowRow) => {
        if (!r.candidate) return;
        const ok = window.confirm(t(
            `Upariti s izvodom ${fmtD(r.candidate.statement_date)} ${fmt(r.candidate.amount)} (${r.candidate.partner_name})?`,
            `Match with statement ${fmtD(r.candidate.statement_date)} ${fmt(r.candidate.amount)}?`,
        ));
        if (!ok) return;
        await fetch(`/api/cashflow/expected-inflows/${r.id}/confirm`, { method: 'POST', headers, body: JSON.stringify({ entry_id: r.candidate.entry_id }) }).catch(() => undefined);
        load();
    };

    const edit = (r: InflowRow) => setForm({
        id: r.id, company_id: String(r.company_id), bank_account_id: r.bank_account_id ? String(r.bank_account_id) : '',
        partner_profile_id: r.partner_profile_id, partner_name: r.partner_name, expected_date: r.expected_date,
        amount: String(r.amount).replace('.', ','), description: r.description ?? '',
    });

    if (err) return <div style={{ color: T.red, padding: 20 }}>{err}</div>;
    if (!data) return <div style={{ color: T.dim, padding: 40, textAlign: 'center' }}>{t('Učitavam priljeve…', 'Loading inflows…')}</div>;

    const accounts = data.accounts.filter((a) => !form?.company_id || String(a.company_id) === form.company_id);
    const tile = (label: string, value: number, color: string, sub?: string) => (
        <div style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, padding: '10px 14px', minWidth: 180 }}>
            <div style={{ fontSize: 11, color: T.dim }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color }}>{fmt(value)}</div>
            {sub ? <div style={{ fontSize: 11, color: T.dim }}>{sub}</div> : null}
        </div>
    );
    const rowBg = (s: InflowRow['status']) => (s === 'overdue' ? 'rgba(192,90,90,0.16)' : s === 'fuzzy' ? 'rgba(201,162,39,0.16)' : undefined);
    const th: React.CSSProperties = { padding: '6px 8px', textAlign: 'left', color: T.gold, fontSize: 11, fontWeight: 600 };
    const td: React.CSSProperties = { padding: '6px 8px', borderTop: `1px solid ${T.line}`, verticalAlign: 'top' };

    return (
        <div style={{ maxWidth: 1300 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                {tile(t('Očekivani priljevi', 'Expected inflows'), data.summary.total, T.ink, `${data.summary.count} ${t('stavki', 'items')}`)}
                {tile(t('Prošao rok, nije na izvodu', 'Overdue, not on statement'), data.summary.overdue, T.red, `${data.summary.overdue_count} ${t('stavki', 'items')}`)}
                {tile(t('Na izvodu izvan 5 %', 'On statement outside 5 %'), data.summary.fuzzy, T.gold, `${data.summary.fuzzy_count} ${t('stavki', 'items')}`)}
                <button type="button" onClick={() => { setForm(form ? null : emptyForm(companyId)); setMsg(''); }}
                    style={{ ...btn, marginLeft: 'auto', alignSelf: 'center', borderColor: T.gold, color: T.gold, fontWeight: 700, fontSize: 13 }}>
                    + {t('Novi priljev', 'New inflow')}
                </button>
            </div>

            {form ? (
                <div style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{form.id ? t('Uredi priljev', 'Edit inflow') : t('Novi priljev', 'New inflow')}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
                        <select value={form.company_id} onChange={(e) => { set('company_id', e.target.value); set('bank_account_id', ''); }} style={field}>
                            <option value="">{t('Tvrtka *', 'Company *')}</option>
                            {data.companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                        </select>
                        <select value={form.bank_account_id} onChange={(e) => set('bank_account_id', e.target.value)} style={field}>
                            <option value="">{t('Račun (bilo koji)', 'Account (any)')}</option>
                            {accounts.map((a) => <option key={a.id} value={a.id}>{(a.bank_name ?? '') + ' ' + a.iban.slice(-6) + (a.account_label ? ` · ${a.account_label}` : '')}</option>)}
                        </select>
                        <div style={{ position: 'relative' }}>
                            <input value={form.partner_name} placeholder={t('Partner * (šifrarnik ili slobodno)', 'Partner *')}
                                onChange={(e) => setForm((f) => (f ? { ...f, partner_name: e.target.value, partner_profile_id: null } : f))} style={field} />
                            {partners.length > 0 ? (
                                <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, background: T.bg, border: `1px solid ${T.line}`, borderRadius: 7, maxHeight: 240, overflowY: 'auto' }}>
                                    {partners.map((p) => (
                                        <div key={p.id} onClick={() => { setForm((f) => (f ? { ...f, partner_name: p.name, partner_profile_id: p.id } : f)); setPartners([]); }}
                                            style={{ padding: '6px 9px', cursor: 'pointer', fontSize: 12, borderTop: `1px solid ${T.line}` }}>
                                            {p.name} <span style={{ color: T.dim }}>{p.oib ?? ''} {p.partner_code ? `#${p.partner_code}` : ''}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                        <DateInput value={form.expected_date} onChange={(v: string) => set('expected_date', v)} style={field} />
                        <input value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder={t('Iznos (€) *', 'Amount (€) *')} style={{ ...field, textAlign: 'right' }} />
                        <input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder={t('Opis', 'Description')} style={field} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                        <button type="button" disabled={busy} onClick={() => void save()} style={{ ...btn, background: T.green, borderColor: T.green, color: '#fff', fontWeight: 700 }}>{busy ? '…' : t('Spremi', 'Save')}</button>
                        <button type="button" onClick={() => setForm(null)} style={btn}>{t('Odustani', 'Cancel')}</button>
                        <span style={{ fontSize: 11, color: T.dim }}>{t('Bez datuma priljev je današnji.', 'Without a date the inflow is today.')}</span>
                        {msg ? <span style={{ color: T.red, fontSize: 12 }}>{msg}</span> : null}
                    </div>
                </div>
            ) : null}

            <div style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, padding: 4 }}>
                {data.rows.length === 0 ? (
                    <div style={{ color: T.dim, fontSize: 13, padding: 16 }}>{t('Nema otvorenih priljeva. Upareni s izvodom su u standardnom pregledu.', 'No open inflows.')}</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead><tr>
                            <th style={th}>{t('Datum', 'Date')}</th><th style={th}>{t('Tvrtka', 'Co.')}</th><th style={th}>{t('Račun', 'Account')}</th>
                            <th style={th}>{t('Partner', 'Partner')}</th><th style={th}>{t('Opis', 'Description')}</th>
                            <th style={{ ...th, textAlign: 'right' }}>{t('Iznos', 'Amount')}</th><th style={th}>{t('Izvod', 'Statement')}</th><th style={th}></th>
                        </tr></thead>
                        <tbody>
                            {data.rows.map((r) => (
                                <tr key={r.id} style={{ background: rowBg(r.status) }}>
                                    <td style={{ ...td, whiteSpace: 'nowrap', color: r.status === 'overdue' ? T.red : T.ink }}>{fmtD(r.expected_date)}</td>
                                    <td style={td}>{r.company_code ?? '-'}</td>
                                    <td style={{ ...td, color: T.dim, whiteSpace: 'nowrap' }}>{r.iban ? `${r.bank_name ?? ''} ${r.iban.slice(-6)}` : '-'}</td>
                                    <td style={td}>{r.partner_name}</td>
                                    <td style={{ ...td, color: T.dim }}>{r.description ?? ''}</td>
                                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(r.amount)}</td>
                                    <td style={{ ...td, fontSize: 12 }}>
                                        {r.status === 'fuzzy' && r.candidate ? (
                                            <span>
                                                {fmtD(r.candidate.statement_date)} {fmt(r.candidate.amount)} <span style={{ color: T.gold }}>({r.candidate.diff_pct} %)</span>{' '}
                                                <button type="button" onClick={() => void confirmMatch(r)} style={{ ...btn, padding: '2px 8px', borderColor: T.gold, color: T.gold }}>{t('Potvrdi', 'Confirm')}</button>
                                            </span>
                                        ) : r.status === 'overdue' ? <span style={{ color: T.red }}>{t('nije na izvodu', 'not on statement')}</span> : <span style={{ color: T.dim }}>{t('čeka', 'waiting')}</span>}
                                    </td>
                                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                                        <button type="button" onClick={() => edit(r)} style={{ ...btn, padding: '2px 8px' }}>{t('Uredi', 'Edit')}</button>{' '}
                                        <button type="button" onClick={() => void del(r.id)} style={{ ...btn, padding: '2px 8px', color: T.red }}>{t('Obriši', 'Delete')}</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
